import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { queryClient } from "../lib/queryClient";
import { authService } from "../services/authService";
import { PERMISSION_IMPLICATIONS } from "../constants/permissionImplications";

interface PermissionSet {
  [key: string]: string[];
}

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  role: string | null;
  permissions: PermissionSet | null;
  user: any | null;
  activeDepartment: string;
  availableDepartments: string[];
  rolesByDepartment: any;
}

interface AuthContextType extends AuthState {
  login: (
    accessToken: string, 
    refreshToken: string, 
    role: string, 
    permissions: PermissionSet, 
    user: any,
    activeDepartment?: string,
    availableDepartments?: string[],
    rolesByDepartment?: any
  ) => void;
  logout: () => void;
  updateUser: (userData: any) => void;
  hasPermission: (module: string, action: string) => boolean;
  refreshAuth: () => Promise<boolean>;
  isTokenExpired: () => boolean;
  isTokenExpiringSoon: () => boolean;
  switchDepartment: (department: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

const ALL_DEPARTMENTS_LIST = ['OMS', 'AMS', 'EPC'];

/**
 * Pick the best activeDepartment for a user based on:
 *   1. saved localStorage value, if it's in the user's available list
 *   2. first item from availableDepartments
 *   3. final fallback to 'OMS' (only if the user has no departments at all)
 *
 * Per spec §4.13 — replaces the prior hardcoded 'OMS' default that prevented
 * PMO-style roles (no OMS) from seeing their menu immediately after login.
 */
export function resolveActiveDepartment(
  availableDepartments: string[],
  savedDepartment?: string | null,
): string {
  if (
    savedDepartment &&
    availableDepartments &&
    availableDepartments.includes(savedDepartment)
  ) {
    return savedDepartment;
  }
  if (availableDepartments && availableDepartments.length > 0) {
    return availableDepartments[0];
  }
  return 'OMS';
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() => {
    const token = authService.getToken();
    const refreshToken = authService.getRefreshToken();
    let role = authService.getRole();
    const permissions = authService.getPermissions();
    const user = authService.getUser();
    const savedActiveDepartment = authService.getActiveDepartment();
    const availableDepartments = authService.getAvailableDepartments();
    const rolesByDepartment = authService.getRolesByDepartment();

    const allDepts = ['OMS', 'AMS', 'EPC'];
    const depts = user?.isSuperAdmin ? allDepts : availableDepartments;
    const activeDepartment = resolveActiveDepartment(depts, savedActiveDepartment);

    // Persisted role string can be empty while user object still has role
    // (older sessions / partial writes). Sync so /dashboard routes Head/Manager correctly.
    if (user && (!role || !String(role).trim())) {
      const deptEntry = rolesByDepartment?.[activeDepartment];
      const fromDept =
        deptEntry && typeof deptEntry === 'object' && 'name' in deptEntry
          ? (deptEntry as { name?: string }).name
          : undefined;
      const resolved =
        (typeof user.role === 'object' && user.role?.name) ||
        user.roleName ||
        fromDept ||
        '';
      if (resolved) {
        role = resolved;
        authService.setRole(resolved);
      }
    }

    return {
      token,
      refreshToken,
      role,
      permissions,
      user,
      activeDepartment,
      availableDepartments: depts,
      rolesByDepartment,
    };
  });

  // Proactive refresh on mount — runs once after the lazy-init renders.
  useEffect(() => {
    if (state.token && state.refreshToken && authService.isTokenExpiringSoon()) {
      refreshAuth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handler = () => {
      if (
        document.visibilityState === 'visible' &&
        state.token &&
        state.refreshToken &&
        authService.isTokenExpiringSoon()
      ) {
        refreshAuth();
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [state.token, state.refreshToken]);

  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key !== 'accessToken') return;
      if (!e.newValue) {
        // Session ended in another tab — this path bypasses logout(), so the
        // cross-user cache clear has to happen here too.
        queryClient.clear();
        setState({
          token: null,
          refreshToken: null,
          role: null,
          permissions: null,
          user: null,
          activeDepartment: 'OMS',
          availableDepartments: ['OMS'],
          rolesByDepartment: {},
        });
      } else if (e.newValue !== state.token) {
        setState({
          token: e.newValue,
          refreshToken: authService.getRefreshToken(),
          role: authService.getRole(),
          permissions: authService.getPermissions(),
          user: authService.getUser(),
          activeDepartment: authService.getActiveDepartment(),
          availableDepartments: authService.getAvailableDepartments(),
          rolesByDepartment: authService.getRolesByDepartment(),
        });
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [state.token]);

  // Set up token refresh interval
  useEffect(() => {
    if (!state.token) return;

    const checkTokenExpiration = async () => {
      if (authService.isTokenExpiringSoon() && state.refreshToken) {
        await refreshAuth();
      }
    };

    // Check every minute
    const interval = setInterval(checkTokenExpiration, 60000);
    return () => clearInterval(interval);
  }, [state.token, state.refreshToken]);

  const login = (
    accessToken: string,
    refreshToken: string,
    role: string,
    permissions: PermissionSet,
    user: any,
    activeDepartment?: string,
    availableDepartments: string[] = ['OMS'],
    rolesByDepartment: any = {}
  ) => {
    // Resolve activeDepartment against availableDepartments. Caller's value
    // wins if it's in the user's list; otherwise we fall back to first
    // available, then 'OMS'. This prevents a user without OMS getting stuck
    // on 'OMS' just because some upstream defaulted it.
    const resolvedActive = resolveActiveDepartment(
      availableDepartments,
      activeDepartment ?? null,
    );

    // Drop any cached queries from a previous session — the query cache
    // survives an SPA logout, and several keys are not user-scoped, so the
    // incoming user would otherwise be served the previous user's data.
    queryClient.clear();

    // Store in authService
    authService.login(
      accessToken,
      refreshToken,
      role,
      permissions,
      user,
      resolvedActive,
      availableDepartments,
      rolesByDepartment
    );

    // Update state
    setState({
      token: accessToken,
      refreshToken,
      role,
      permissions,
      user,
      activeDepartment: resolvedActive,
      availableDepartments,
      rolesByDepartment
    });
  };

  const logout = () => {
    // Clear before anything else: cached queries must never outlive the session.
    queryClient.clear();
    authService.logout();
    setState({ 
      token: null, 
      refreshToken: null, 
      role: null, 
      permissions: null, 
      user: null,
      activeDepartment: 'OMS',
      availableDepartments: ['OMS'],
      rolesByDepartment: {}
    });
  };

  const updateUser = (userData: any) => {
    // Update user data in authService
    authService.updateUser(userData);
    
    // Update state
    setState(prevState => ({
      ...prevState,
      user: { ...prevState.user, ...userData }
    }));
  };

  const refreshAuth = async (): Promise<boolean> => {
    try {
      const success = await authService.refreshToken();
      if (success) {
        // Update state with new token data
        const authData = authService.getAuthData();

        const depts = authData.user?.isSuperAdmin
          ? ALL_DEPARTMENTS_LIST
          : (authData.availableDepartments || ['OMS']);
        const resolvedActive = resolveActiveDepartment(
          depts,
          authData.activeDepartment,
        );
        if (resolvedActive !== authData.activeDepartment) {
          authService.setActiveDepartment(resolvedActive);
        }
        setState({
          token: authData.token,
          refreshToken: authData.refreshToken,
          role: authData.role,
          permissions: authData.permissions,
          user: authData.user,
          activeDepartment: resolvedActive,
          availableDepartments: depts,
          rolesByDepartment: authData.rolesByDepartment || {}
        });
        return true;
      } else {
        // Refresh failed, logout user
        logout();
        return false;
      }
    } catch (error: any) {
      console.error('Error refreshing auth:', error);
      // Only logout on definitive refresh rejection (401/400).
      // 5xx and network errors preserve the session so the user can retry.
      const status = error?.response?.status;
      if (status === 401 || status === 400) {
        logout();
      }
      return false;
    }
  };

  const ALL_DEPARTMENTS = ALL_DEPARTMENTS_LIST;

  const switchDepartment = async (department: string): Promise<void> => {
    try {
      const result = await authService.switchDepartment(department);

      // Build updated rolesByDepartment from refreshed user data
      const updatedRolesByDepartment = result.user.roles
        ? result.user.roles.reduce((acc: any, curr: any) => ({ ...acc, [curr.department]: curr }), {})
        : { [department]: result.user.role };

      // Super Admin: keep all departments in switcher; others use roles from API
      const isSuperAdmin = !!(result.user as any).isSuperAdmin;
      const updatedAvailableDepartments = isSuperAdmin
        ? ALL_DEPARTMENTS
        : result.user.roles
          ? result.user.roles.map((r: any) => r.department)
          : [department];

      // The backend now returns the role for the NEW active department.
      // Web browsers receive the refresh token via HttpOnly cookie so it may not
      // be in the response body – fall back to the previously stored token.
      const newRefreshToken = result.tokens.refresh?.token ?? state.refreshToken;

      setState(prevState => ({
        ...prevState,
        token: result.tokens.access.token,
        refreshToken: newRefreshToken,
        role: result.user.role?.name || '',
        permissions: result.user.role?.permissions || {},
        user: result.user,
        activeDepartment: department,
        availableDepartments: updatedAvailableDepartments,
        rolesByDepartment: updatedRolesByDepartment,
      }));
    } catch (error) {
      console.error('Error switching department:', error);
      throw error;
    }
  };

  const hasPermission = (module: string, action: string): boolean => {
    // Super Admin Bypass
    if (state.user?.isSuperAdmin) {
      return true;
    }

    if (!state.permissions) {
      console.warn('hasPermission: No permissions available for module:', module);
      return false;
    }

    // Admin override - admin should have all permissions
    if (state.role?.toLowerCase() === 'admin') {
      return true;
    }

    const directHit = state.permissions[module]?.includes(action) ?? false;
    if (directHit) return true;

    // Permission implications — mirror of backend `PERMISSION_IMPLICATIONS`
    // (src/modules/core/role/role-context.ts). Keep both lists in sync.
    const target = `${module}.${action}`;
    const hasImplication = PERMISSION_IMPLICATIONS.some((impl) => {
      if (!impl.grants.includes(target)) return false;
      const [implModule, implAction] = impl.when.split('.');
      if (!implModule || !implAction) return false;
      return state.permissions?.[implModule]?.includes(implAction) ?? false;
    });
    if (hasImplication) return true;

    console.warn('hasPermission: DENIED', { module, action, available: Object.keys(state.permissions) });
    return false;
  };

  const isTokenExpired = (): boolean => {
    return authService.isTokenExpired();
  };

  const isTokenExpiringSoon = (): boolean => {
    return authService.isTokenExpiringSoon();
  };

  return (
    <AuthContext.Provider value={{ 
      ...state, 
      login, 
      logout, 
      updateUser,
      hasPermission, 
      refreshAuth, 
      isTokenExpired, 
      isTokenExpiringSoon,
      switchDepartment
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}